import React, { useCallback } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment-timezone';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import { consumptionAmendmentSchema, consumptionAmendmentUISchema } from '../../schemas/consumptionAmendmentSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert';
import Loading from '../../../Loading/Loading';

const ConsumptionAmendmentForm = ({ onClose, initialData }) => {
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});

  const handleChange = useCallback(
    ({ formData }) => {
      updateFormContext(formData);
    },
    [updateFormContext]
  );

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.period_start_date':
          return { ...error, message: 'Period Start Date is a required property' };
        case '.period_end_date':
          return { ...error, message: 'Period End Date is a required property' };
        case '.measurement_quantity_id':
          return { ...error, message: 'Measurement Quantity ID is a required property' };
        case '.gross_revised_consumption':
          return { ...error, message: 'Gross Revised Consumption is a required property' };
        case '.reason_code':
          return { ...error, message: 'Reason Code is a required property' };
        default:
          return error;
      }
    });
  }, []);

  const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return null;
  
      const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateTimeString);
      const parsedDate = hasTimezone
        ? moment.parseZone(dateTimeString)
        : moment.tz(dateTimeString, 'Europe/London');
  
      if (!parsedDate.isValid()) return null;
  
      return parsedDate.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ssZ');
    };

  const formatConsumption = (value) => {
    if (value === null || value === undefined) return null;
    const strValue = String(value);
    const moreThanThreeNonZero = strValue.match(/\.\d{0,3}[1-9]\d*/);
    const decimalPart = strValue.includes('.') ? strValue.split('.')[1] : '';
    if (decimalPart.length > 3 && moreThanThreeNonZero) {
      return strValue;
    }
    return parseFloat(value).toFixed(3);
  };

  const validateConsumption = (value) => {
    if (value === null || value === undefined) return false;
    const strValue = String(value);
    // Check if it has exactly 3 decimal places
    const decimalMatch = strValue.match(/^-?\d{1,11}(\.\d{3})$/);
    return decimalMatch !== null;
  };

  const customValidate = (formData, errors) => {
    // Validate gross_revised_consumption format (exactly 3 decimal places)
    if (formData.gross_revised_consumption !== undefined && formData.gross_revised_consumption !== null) {
      const formatted = formatConsumption(formData.gross_revised_consumption);
      if (!validateConsumption(formatted)) {
        errors.gross_revised_consumption.addError('Must have exactly 3 decimal places (e.g., 13.123)');
      }
    }

    // Validate period_end_date is after period_start_date
    if (formData.period_start_date && formData.period_end_date) {
      const startDate = new Date(formData.period_start_date);
      const endDate = new Date(formData.period_end_date);
      if (endDate <= startDate) {
        errors.period_end_date.addError('Period End Date must be after Period Start Date');
      }
    }

    // Validate supplier_reference max length
    if (formData.supplier_reference && formData.supplier_reference.length > 50) {
      errors.supplier_reference.addError('Supplier Reference must not exceed 50 characters');
    }

    return errors;
  };

  const transformFormDataToPayload = (formData) => {
    const payload = {
      mpan_core: parseInt(formData.mpan_core, 10),
      period_start_date: formatDateTime(formData.period_start_date),
      period_end_date: formatDateTime(formData.period_end_date),
      measurement_quantity_id: formData.measurement_quantity_id,
      gross_revised_consumption: formatConsumption(formData.gross_revised_consumption),
      reason_code: formData.reason_code,
    };

    // Add supplier_reference if provided
    if (formData.supplier_reference && formData.supplier_reference.trim()) {
      payload.supplier_reference = formData.supplier_reference.trim();
    }

    return payload;
  };

  const uiSchemaWithWidgets = {
    ...consumptionAmendmentUISchema,
    mpan_core: {
      ...consumptionAmendmentUISchema.mpan_core,
      'ui:widget': (props) => (
        <MeterPointSelector {...props} placeholder='Select MPAN' statusFilter='L' includeAllStatuses={false} />
      ),
    },
  };

  return (
    <div className='consumption-amendment-form'>
      <Form
        schema={consumptionAmendmentSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        validate={customValidate}
        showErrorList={false}
        onSubmit={({ formData }) => {
          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);

          // eslint-disable-next-line no-console
          console.log('Submitting payload to BOP.Dataflows.StartConsumptionAmendment:', payload);

          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.StartConsumptionAmendment' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Consumption Amendment request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
              }
            })
            .catch((error) => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.error('Error submitting consumption amendment request:', error);
              // eslint-disable-next-line no-console
              console.log('Publishing to queue BOP.Dataflows.StartConsumptionAmendment:', payload);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div'))
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && <Loading />}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ConsumptionAmendmentForm;
